/**
 * @fileoverview Provides theme configuration and color scheme management for
 * the application, supporting both light and dark modes with system preference
 * detection.
 */

import * as React from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { ThemeProvider, useColorScheme } from '@mui/material/styles';
import InitColorSchemeScript from '@mui/material/InitColorSchemeScript';
import CssBaseline from '@mui/material/CssBaseline';
import { PaletteModeContext } from '../contexts';
import { usePaletteModeContext } from '../hooks/usePaletteModeContext';

const COLOR_SCHEME_STORAGE_KEY = 'toolpad-color-scheme';
const MODE_STORAGE_KEY = 'toolpad-mode';

type PaletteMode = 'light' | 'dark';

/**
 * Hook to detect user's preferred color scheme.
 */
function usePreferredMode(window?: Window): PaletteMode {
  const prefersDarkMode = useMediaQuery(
    '(prefers-color-scheme: dark)',
    window && {
      matchMedia: window.matchMedia,
    },
  );
  return prefersDarkMode ? 'dark' : 'light';
}

interface PaletteModeProviderProps {
  children: React.ReactNode;
  window?: Window;
}

function PaletteModeProvider(props: PaletteModeProviderProps): React.JSX.Element {
  const { children, window: appWindow } = props;

  const preferredMode = usePreferredMode(appWindow);
  const { mode, setMode, allColorSchemes } = useColorScheme();

  const effectivePaletteMode = !mode || mode === 'system' ? preferredMode : mode;
  const isDualTheme = allColorSchemes.length > 1;
  const paletteModeContextValue = usePaletteModeContext(effectivePaletteMode, setMode, isDualTheme);

  return (
    <PaletteModeContext.Provider value={paletteModeContextValue}>
      {children}
    </PaletteModeContext.Provider>
  );
}

interface AppThemeProviderProps {
  children: React.ReactNode;
  theme: Theme;
  window?: Window;
  nonce?: string;
}

function AppThemeProvider(props: AppThemeProviderProps): React.JSX.Element {
  const { children, theme, window: appWindow, nonce } = props;

  return (
    <ThemeProvider
      theme={theme}
      documentNode={appWindow?.document}
      colorSchemeNode={appWindow?.document.documentElement}
      disableNestedContext
      colorSchemeStorageKey={COLOR_SCHEME_STORAGE_KEY}
      modeStorageKey={MODE_STORAGE_KEY}
    >
      <InitColorSchemeScript
        attribute={theme.colorSchemeSelector}
        colorSchemeStorageKey={COLOR_SCHEME_STORAGE_KEY}
        modeStorageKey={MODE_STORAGE_KEY}
        nonce={nonce}
      />
      <PaletteModeProvider window={appWindow}>
        <CssBaseline enableColorScheme />
        {children}
      </PaletteModeProvider>
    </ThemeProvider>
  );
}

export { AppThemeProvider };

